import { bubbleCentre } from "./BubbleGeometry";
import {
  getShoutBubbleBodyPoints,
  getSpeechBubbleTailPoints,
  getThoughtBubbleBodyPoints,
  getThoughtBubbleTailCircles,
} from "./BubbleVisualGeometry";
import { layoutBubbleText } from "./BubbleTextLayout";
import { getPanelTemplateMetrics } from "./PanelTemplates";
import type {
  ComicBubbleObject,
  ComicPanel,
  ComicSelectionState,
  PersistedDocumentState,
} from "./comicTypes";

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.roundRect(x, y, width, height, safeRadius);
}

function bubblePath(context: CanvasRenderingContext2D, object: ComicBubbleObject): void {
  const { width, height, type } = object;
  if (type === "narrationBox") {
    context.beginPath();
    context.rect(-width / 2, -height / 2, width, height);
    return;
  }
  if (type === "thoughtBubble") {
    context.beginPath();
    getThoughtBubbleBodyPoints(object).forEach((point, index) => {
      if (index === 0) context.moveTo(point.x, point.y);
      else context.lineTo(point.x, point.y);
    });
    context.closePath();
    return;
  }
  if (type === "shoutBubble") {
    context.beginPath();
    getShoutBubbleBodyPoints(object).forEach((point, index) => {
      if (index === 0) context.moveTo(point.x, point.y);
      else context.lineTo(point.x, point.y);
    });
    context.closePath();
    return;
  }
  roundedRect(context, -width / 2, -height / 2, width, height, Math.min(width, height) * 0.28);
}

function renderTail(context: CanvasRenderingContext2D, object: ComicBubbleObject): void {
  if (object.type === "narrationBox" || object.type === "shoutBubble") return;
  context.beginPath();
  if (object.type === "thoughtBubble") {
    for (const circle of getThoughtBubbleTailCircles(object)) {
      context.moveTo(circle.x + circle.radius, circle.y);
      context.arc(circle.x, circle.y, circle.radius, 0, Math.PI * 2);
    }
    context.fill();
    context.stroke();
    return;
  }
  getSpeechBubbleTailPoints(object).forEach((point, index) => {
    if (index === 0) context.moveTo(point.x, point.y);
    else context.lineTo(point.x, point.y);
  });
  context.closePath();
  context.fill();
  context.stroke();
}

export function renderComicObject(
  context: CanvasRenderingContext2D,
  object: ComicBubbleObject,
): void {
  const centre = bubbleCentre(object);
  context.save();
  context.translate(centre.x, centre.y);
  context.rotate((object.rotation * Math.PI) / 180);
  context.fillStyle = object.data.fillColor;
  context.strokeStyle = object.data.strokeColor;
  context.lineWidth = object.data.strokeWidth;
  context.lineJoin = "round";
  bubblePath(context, object);
  context.fill();
  context.stroke();
  renderTail(context, object);

  const paddingX = Math.max(12, object.width * 0.11);
  const paddingY = Math.max(10, object.height * 0.13);
  const textWidth = Math.max(1, object.width - paddingX * 2);
  const textHeight = Math.max(1, object.height - paddingY * 2);
  const fontWeight = object.type === "shoutBubble" ? 700 : 600;
  const measure = (text: string, fontSize: number): number => {
    context.font = `${fontWeight} ${fontSize}px system-ui, sans-serif`;
    return context.measureText(text).width;
  };
  const layout = layoutBubbleText(
    object.data.text,
    object.data.fontSize,
    textWidth,
    textHeight,
    measure,
  );
  context.beginPath();
  context.rect(-object.width / 2 + paddingX, -object.height / 2 + paddingY, textWidth, textHeight);
  context.clip();
  context.fillStyle = "#202235";
  context.font = `${fontWeight} ${layout.fontSize}px system-ui, sans-serif`;
  context.textAlign = object.data.textAlign;
  context.textBaseline = "middle";
  const startY = -((layout.lines.length - 1) * layout.lineHeight) / 2;
  const textX = object.data.textAlign === "left" ? -object.width / 2 + paddingX : 0;
  layout.lines.forEach((line, index) => {
    const visibleLine = layout.overflow && index === layout.lines.length - 1 ? `${line}…` : line;
    context.fillText(visibleLine, textX, startY + index * layout.lineHeight, textWidth);
  });
  context.restore();
}

export function renderPanelBoundaries(
  context: CanvasRenderingContext2D,
  panels: ReadonlyArray<ComicPanel>,
  pageWidth: number,
  pageHeight: number,
): void {
  const { borderWidth } = getPanelTemplateMetrics(pageWidth, pageHeight);
  context.save();
  context.strokeStyle = "#202235";
  context.lineWidth = borderWidth;
  context.lineJoin = "miter";
  for (const panel of panels) context.strokeRect(panel.x, panel.y, panel.width, panel.height);
  context.restore();
}

export function renderComicDocument(
  context: CanvasRenderingContext2D,
  state: PersistedDocumentState,
  pageWidth: number,
  pageHeight: number,
): void {
  if (!state.comicTemplateId) return;
  for (const object of [...state.objects].sort((left, right) => left.order - right.order)) {
    const panel = state.panels.find((candidate) => candidate.id === object.panelId);
    if (!panel) continue;
    context.save();
    context.beginPath();
    context.rect(panel.x, panel.y, panel.width, panel.height);
    context.clip();
    renderComicObject(context, object);
    context.restore();
  }
  // Keep the stable panel frame visible above every clipped dialogue object.
  renderPanelBoundaries(context, state.panels, pageWidth, pageHeight);
}

export function renderComicSelection(
  context: CanvasRenderingContext2D,
  selection: ComicSelectionState,
  panels: ReadonlyArray<ComicPanel>,
  zoom: number,
): void {
  const panel = panels.find((candidate) => candidate.id === selection.panelId);
  context.save();
  if (panel) {
    context.strokeStyle = "#625bff";
    context.lineWidth = 4 / Math.max(zoom, 0.05);
    context.setLineDash([12 / zoom, 8 / zoom]);
    context.strokeRect(panel.x, panel.y, panel.width, panel.height);
    context.setLineDash([]);
  }
  if (selection.object) {
    const object = selection.object;
    const centre = bubbleCentre(object);
    const radius = 22 / Math.max(zoom, 0.05);
    context.translate(centre.x, centre.y);
    context.rotate((object.rotation * Math.PI) / 180);
    context.strokeStyle = "#625bff";
    context.lineWidth = 3 / Math.max(zoom, 0.05);
    context.strokeRect(-object.width / 2, -object.height / 2, object.width, object.height);
    context.fillStyle = "#ffffff";
    context.beginPath();
    context.arc(object.width / 2, object.height / 2, radius, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.beginPath();
    context.moveTo(0, -object.height / 2);
    context.lineTo(0, -object.height / 2 - radius * 1.8);
    context.stroke();
    context.beginPath();
    context.arc(0, -object.height / 2 - radius * 1.8, radius, 0, Math.PI * 2);
    context.fill();
    context.stroke();
  }
  context.restore();
}
